import React, { useState, useEffect } from 'react';
import { AlertCircle, X, Bot, Check, Plus, Loader2, Info } from 'lucide-react';
import { authService } from '../services/authService';
import { getApiBaseUrl } from '../services/api';
import { AdminLoading } from './admin/AdminLoading';
import { AdminPageHeader } from './admin/AdminPageHeader';
import { AdminAddButton } from './admin/AdminAddButton';
import { useConfirmDialog } from './admin/ConfirmDialog';
import { AdminActionButton, AdminRowActions } from './admin/AdminActionButton';

const DefaultIntentManagement = ({ defaultStore }) => {
  const [defaultIntents, setDefaultIntents] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(defaultStore || null);
  const [selectedDefaultIntents, setSelectedDefaultIntents] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { confirm, confirmDialog } = useConfirmDialog();
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDefaultIntent, setEditingDefaultIntent] = useState(null);
  const [formData, setFormData] = useState({
    intentName: '',
    category: '',
    description: '',
    enabled: true
  });
  const [isInjecting, setIsInjecting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const isSuperAdmin = authService.isSuperAdmin();

  useEffect(() => {
    fetchDefaultIntents();
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/stores`, {
        headers: authService.getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStores(data);
      }
    } catch (err) {
      console.error('Error fetching stores:', err);
    }
  };

  const fetchDefaultIntents = async () => {
    setError('');
    setLoading(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/default-intents`, {
        headers: authService.getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDefaultIntents(data);
      } else {
        setError('Failed to fetch default intents');
      }
    } catch (err) {
      setError('Error fetching default intents');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const url = editingDefaultIntent
        ? `${getApiBaseUrl()}/admin/default-intents/${editingDefaultIntent.id}`
        : `${getApiBaseUrl()}/admin/default-intents`;

      const method = editingDefaultIntent ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchDefaultIntents();
        handleCloseDialog();
        setSuccess(editingDefaultIntent ? 'Default intent updated successfully' : 'Default intent created successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to save default intent');
      }
    } catch (err) {
      setError('Error saving default intent');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (defaultIntent) => {
    setError('');
    setSuccess('');
    setEditingDefaultIntent(defaultIntent);
    setFormData({
      intentName: defaultIntent.intentName,
      category: defaultIntent.category || '',
      description: defaultIntent.description || '',
      enabled: defaultIntent.enabled
    });
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    const defaultIntent = defaultIntents.find((i) => i.id === id);
    const label = defaultIntent?.intentName ? `"${defaultIntent.intentName}"` : 'this default intent';
    const confirmed = await confirm({
      title: `Delete default intent ${label}?`,
      message: `The default intent ${label} will be permanently deleted.`,
      confirmLabel: 'Delete',
    });
    if (!confirmed) return;
    setDeletingId(id);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/default-intents/${id}`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders()
      });
      if (response.ok) {
        await fetchDefaultIntents();
        setSuccess('Default intent deleted successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to delete default intent');
      }
    } catch (err) {
      setError('Error deleting default intent');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingDefaultIntent(null);
    setFormData({
      intentName: '',
      category: '',
      description: '',
      enabled: true
    });
    setError('');
    setSuccess('');
  };

  const handleToggleEnabled = async (defaultIntent) => {
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/default-intents/${defaultIntent.id}`, {
        method: 'PUT',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...defaultIntent,
          enabled: !defaultIntent.enabled
        })
      });
      if (response.ok) {
        await fetchDefaultIntents();
      } else {
        setError('Failed to toggle default intent');
      }
    } catch (err) {
      setError('Error toggling default intent');
    }
  };

  const handleInjectToStore = async () => {
    if (!selectedStore) {
      setError('Please select a store first');
      return;
    }
    if (selectedDefaultIntents.size === 0) {
      setError('Please select at least one default intent to inject');
      return;
    }

    setIsInjecting(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/default-intents/inject-to-store`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          storeId: selectedStore,
          defaultIntentIds: Array.from(selectedDefaultIntents)
        })
      });
      if (response.ok) {
        setSelectedDefaultIntents(new Set());
        const count = selectedDefaultIntents.size;
        setSuccess(`${count} default intent${count !== 1 ? 's' : ''} injected to store successfully`);
        setTimeout(() => setSuccess(''), 4000);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to inject default intents');
      }
    } catch (err) {
      setError('Error injecting default intents');
    } finally {
      setIsInjecting(false);
    }
  };

  const handleSelectDefaultIntent = (id) => {
    setSelectedDefaultIntents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedDefaultIntents.size === defaultIntents.length) {
      setSelectedDefaultIntents(new Set());
    } else {
      setSelectedDefaultIntents(new Set(defaultIntents.map(di => di.id)));
    }
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Default Intents"
        description="Manage default intents that can be injected to stores"
      />

      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-800 leading-relaxed">
          Default intents are reusable templates. Select a store and the intents you want to copy, then click <strong>Inject Selected</strong> to add them to that store's active intent list.
        </div>
      </div>

      {success && (
        <div className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg animate-fadeIn">
          <Check className="w-4 h-4 text-green-600 mr-2" />
          <span className="text-sm text-green-700">{success}</span>
        </div>
      )}

      {error && (
        <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      {/* Store Selection and Inject Section */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Store for Injection
            </label>
            <select
              value={selectedStore || ''}
              onChange={(e) => setSelectedStore(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">Select a store</option>
              {stores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name || store.storeId}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={handleInjectToStore}
              disabled={isInjecting || !selectedStore || selectedDefaultIntents.size === 0}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm transition"
            >
              {isInjecting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus size={16} />
              )}
              {isInjecting ? 'Injecting...' : 'Inject Selected'}
            </button>
          </div>
        </div>
      </div>

      {/* Default Intents List */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h3 className="text-sm font-medium text-gray-900">
              Default Intents ({defaultIntents.length})
            </h3>
            <button
              onClick={handleSelectAll}
              className="text-sm text-purple-600 hover:text-purple-700"
            >
              {selectedDefaultIntents.size === defaultIntents.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          {isSuperAdmin && <AdminAddButton onClick={() => setOpenDialog(true)} />}
        </div>

        {loading ? (
          <AdminLoading />
        ) : defaultIntents.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Bot className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p className="text-sm">No default intents configured yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {defaultIntents.map((defaultIntent) => (
              <div key={defaultIntent.id} className="p-4 flex items-start justify-between hover:bg-gray-50 transition">
                <div className="flex items-start gap-3 flex-1">
                  <input
                    type="checkbox"
                    checked={selectedDefaultIntents.has(defaultIntent.id)}
                    onChange={() => handleSelectDefaultIntent(defaultIntent.id)}
                    className="mt-1 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-medium text-gray-900">
                        {defaultIntent.intentName}
                      </h4>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        defaultIntent.enabled
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {defaultIntent.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                      {defaultIntent.category && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                          {defaultIntent.category}
                        </span>
                      )}
                    </div>
                    {defaultIntent.description && (
                      <p className="text-sm text-gray-500 mt-1">{defaultIntent.description}</p>
                    )}
                  </div>
                </div>
                {isSuperAdmin && (
                  <AdminRowActions className="ml-4">
                    <AdminActionButton variant="edit" onClick={() => handleEdit(defaultIntent)} />
                    <AdminActionButton variant={defaultIntent.enabled ? 'deactivate' : 'activate'} label={defaultIntent.enabled ? 'Disable' : 'Enable'} onClick={() => handleToggleEnabled(defaultIntent)} />
                    <AdminActionButton variant="delete" loading={deletingId === defaultIntent.id} loadingLabel="Deleting…" onClick={() => handleDelete(defaultIntent.id)} />
                  </AdminRowActions>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      {openDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">
                {editingDefaultIntent ? 'Edit Default Intent' : 'Create Default Intent'}
              </h3>
              <button
                onClick={handleCloseDialog}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {error && (
                <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                  <span className="text-xs text-red-700">{error}</span>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Intent Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.intentName}
                  onChange={(e) => setFormData({ ...formData, intentName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., SHUTTLE_SERVICE_INQUIRY"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Service, Sales, General"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows="3"
                  placeholder="Describe when this intent should be used"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                    id="enabled"
                    checked={formData.enabled}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <label htmlFor="enabled" className="ml-2 text-sm text-gray-700">
                    Enabled
                  </label>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseDialog}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {saving ? 'Saving...' : (editingDefaultIntent ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmDialog}
    </div>
  );
};

export default DefaultIntentManagement;
